import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  forEachRight,
  every,
  get,
  noop,
  some,
  isEmpty,
  filter,
  map,
  last,
} from 'lodash';
import { connect } from 'react-redux';
import { Button, InputField, Icon } from '@unite-us/ui';
import { validations } from '@unite-us/app-components';
import { ShareDrawer } from '@unite-us/shares-utils';
import {
  pluckSelectedProgramIds,
} from 'src/components/Referrals/ReferralGroupsPrograms/utils';
import { env } from 'src/config/env';
import { coreApi } from 'src/api/config';
import { getGroup } from 'common/utils/stateHelpers';
import { ReferralProgramGroupSelect } from '../../ReferralGroupsPrograms/components';
import './stylesheets/oonGroupsSelector.scss';
import { canAddMoreOONGroups, oonGroupSelected, canAddMoreCustomPrograms } from './utils';
import AddProgramsSection from '../../../Browse/AddProgramsSection/AddProgramsSection';
import { getInvalidSensitiveErrors } from '../MimicReferralGroupValidations/utils';

const allGroupValuesAreEmpty = (selected = []) => (
  selected.length > 1 && every(selected, ({ group } = {}) => group.value === '')
);

export class OONGroupsSelector extends Component {
  constructor(props) {
    super(props);

    this.onGroupSelect = this.onGroupSelect.bind(this);
    this.onProgramGroupSelect = this.onProgramGroupSelect.bind(this);
    this.onDeselect = this.onDeselect.bind(this);
    this.addGroupField = this.addGroupField.bind(this);
    this.addCustomField = this.addCustomField.bind(this);
    this.clearCustomField = this.clearCustomField.bind(this);
    this.removeGroupField = this.removeGroupField.bind(this);
    this.toggleShareDrawer = this.toggleShareDrawer.bind(this);
    this.state = {
      disableOrgAddButton: false,
      showShareDrawer: false,
      hasSensitiveError: false,
    };
    this.inputField = React.createRef();
  }

  componentDidMount() {
    this.props.registerField(this.inputField.current);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const selected = get(nextProps, 'fields.oonCase.selected', []);
    const custom = get(nextProps, 'fields.oonCase.custom', []);

    if (selected.length === 0) {
      this.addGroupField();
    }

    if (custom.length === 0) {
      this.addCustomField();
    }

    this.setState({
      disableOrgAddButton: some(selected, ['group.value', '']),
    });

    if (allGroupValuesAreEmpty(selected)) {
      forEachRight(selected, (selectedObj, index) => {
        if (index !== 0) {
          this.removeGroupField(index);
        }
      });
    }

    this.evaluateSelectedFields();
    this.checkSensitiveErrors();
  }

  componentWillUnmount() {
    this.props.unregisterField(this.inputField.current);
  }

  onGroupSelect(selected, index) {
    if (!selected) {
      this.onDeselect(index);
    }
    this.props.onGroupSelect(selected);
    this.evaluateSelectedFields();
    this.checkSensitiveErrors();
  }

  onProgramGroupSelect(selected, index) {
    const { selectedFields } = this.props;
    const group = get(selected, 'relationships.provider.data', '');

    if (!selected) {
      this.onDeselect(index);
    } else {
      selectedFields[index].group.onChange(group);
      selectedFields[index].program.onChange(selected);
    }
    this.props.onGroupSelect(group);
    this.evaluateSelectedFields();
    this.checkSensitiveErrors();
  }

  onDeselect(index) {
    const {
      canPaginateNetworkGroups,
      fields,
      removeSelectedBrowseGroup,
    } = this.props;

    if (canPaginateNetworkGroups) {
      removeSelectedBrowseGroup('out-of-network');
    }
    fields.oonCase.selected.removeField(index);
    this.checkSensitiveErrors(index);
  }

  onShareEvent({ shareMethod }) {
    if (shareMethod === 'print') this.toggleShareDrawer();
  }

  checkSensitiveErrors() {
    const {
      caseReferrals,
      groupKey,
      index,
      oonGroups,
      referral,
      selectedFields,
      sensitiveProvider,
      setHasSensitiveErrors,
    } = this.props;

    const { hasSensitiveError } = this.state;

    const sensitiveGroups = filter(oonGroups, (group) => group.sensitive);
    const sensitiveGroupIds = map(sensitiveGroups, (g) => g.id);

    const hasError = selectedFields.some((field) => getInvalidSensitiveErrors({
      caseReferrals,
      sensitiveGroupIds,
      isCurrentUserGroupSensitive: sensitiveProvider,
      fields: selectedFields,
      groupId: field[groupKey].value.id,
      referral,
    }).length > 0);

    if (hasError !== hasSensitiveError) {
      this.setState({ hasSensitiveError: hasError }, () => {
        if (setHasSensitiveErrors) {
          setHasSensitiveErrors(hasError, index);
        }
      });
    }

    return hasError;
  }

  addGroupField() {
    this.props.fields.oonCase.selected.addField();
  }

  addCustomField() {
    this.props.fields.oonCase.custom.addField();
  }

  clearCustomField(index) {
    const { fields } = this.props;

    fields.oonCase.custom.removeField(index);
  }

  removeGroupField(index) {
    this.onGroupSelect(null, index);
  }

  evaluateSelectedFields() {
    const selectedGroups = this.props.selectedFields.filter((group) => group.group.value);

    if (selectedGroups.length === 0) {
      this.setState({
        showShareDrawer: false,
      });
    }
  }

  toggleShareDrawer() {
    this.setState({ showShareDrawer: !this.state.showShareDrawer });
  }

  render() {
    const {
      currentProviderId,
      employeeId,
      groupKey,
      originCoordinates,
      oonGroups,
      debouncedSearchNetworkGroups,
      referral,
      registerField,
      selectedFields,
      programKey,
      customFields,
      unregisterField,
      isFetchingNetworkGroups,
      sensitiveProvider,
      caseReferrals,
      setDisableNextButton,
    } = this.props;

    const sensitiveGroups = filter(oonGroups, (group) => group.sensitive);
    const sensitiveGroupIds = map(sensitiveGroups, (g) => g.id);
    const sensitiveFieldErrorsFilter = (field) => getInvalidSensitiveErrors({
      caseReferrals,
      sensitiveGroupIds,
      isCurrentUserGroupSensitive: sensitiveProvider,
      fields: selectedFields,
      groupId: field[groupKey].value.id,
      referral,
    }).length > 0;

    const sensitiveErrorsByField = selectedFields
      .filter(sensitiveFieldErrorsFilter)
      .map((field) => (
        {
          id: field[groupKey].value.id,
          errors: getInvalidSensitiveErrors({
            caseReferrals,
            sensitiveGroupIds,
            isCurrentUserGroupSensitive: sensitiveProvider,
            fields: selectedFields,
            groupId: field[groupKey].value.id,
            referral,
          }),
        }
      ))
      .reduce((acc, current) => {
        const result = { ...acc };
        result[current.id] = current.errors;
        return result;
      }, {});

    const sensitiveErrorKey = last(Object.keys(sensitiveErrorsByField));
    const sensitiveFieldErrors = sensitiveErrorsByField[sensitiveErrorKey];
    const { disableOrgAddButton, showShareDrawer } = this.state;
    const hasAtLeastOneCustomProgram = some(customFields, (field) => (get(field, 'group.value') !== ''));
    const hasAtLeastOneProgram = some(selectedFields, (field) => (get(field, 'program.value') !== ''));

    const customOnly = oonGroups.length === 0;

    const shareDrawerProgramsList = selectedFields
      .filter((group) => !isEmpty(group.program?.value))
      .map(({ program: { value: { id, attributes: { name } } } }) => ({ id, name }));

    const sensitivePrograms = selectedFields
      .filter((group) => !isEmpty(group.program?.value))
      .filter(({ program: { value: { attributes: { is_sensitive } } } }) => is_sensitive)
      .map(({ program: { value: { id } } }) => id);

    // As long as we have at least 1 oon group selected/custom group entered
    // we can allow the form to submit with additional blank group fields.
    const hasAtLeastOneValue = some(selectedFields, (field) => (get(field, 'group.value') !== ''));

    // disable the next button if there are no programs selected here (CRTB-1488)
    if (!hasAtLeastOneCustomProgram && !hasAtLeastOneProgram && !hasAtLeastOneValue) {
      setDisableNextButton(true);
    } else {
      setDisableNextButton(false);
    }

    return (
      <div className="oon-groups-selector">
        <div className="row">
          <div className="col-xs-12">
            <AddProgramsSection
              toggleBrowse={this.props.toggleBrowse}
              isFetchingNetworkGroups={isFetchingNetworkGroups}
            />
          </div>
        </div>

        <div className="referral-groups-programs__wrapper">
          {
            selectedFields.map((selected, index) => (
              <div className="mb-8" key={selected.program.value.id ?? index}>
                <ReferralProgramGroupSelect
                  allowEmptyGroups={hasAtLeastOneCustomProgram || hasAtLeastOneProgram}
                  registerField={registerField}
                  groupErrors={isEmpty(sensitiveFieldErrors) ? [] : sensitiveFieldErrors}
                  debouncedSearchNetworkGroups={debouncedSearchNetworkGroups}
                  hidden={customOnly}
                  index={index}
                  onProgramGroupSelect={this.onProgramGroupSelect}
                  originCoordinates={originCoordinates}
                  programField={selected[programKey]}
                  selectedProgramIds={pluckSelectedProgramIds(selectedFields)}
                  suggestedGroups={oonGroups}
                  referral={referral}
                  groupsOptionType="out-of-network"
                  unregisterField={unregisterField}
                />
              </div>
              ))
            }
          <div className={'row -mt-4 mb-2'}>
            <div className="col-xs-12">
              {
                  canAddMoreOONGroups({ selectedFields, suggestedGroups: oonGroups }) && (
                    <Button
                      id="add-another-oon-group-btn"
                      className="mb-one mr-one"
                      label={'+ ADD ANOTHER PROGRAM BY QUICK ADD'}
                      onClick={this.addGroupField}
                      disabled={disableOrgAddButton}
                    />
                  )
                }
              {
                  oonGroupSelected(selectedFields) && !customOnly && (
                    <Button
                      className="share-button referral-groups-programs__share-btn"
                      id="share-btn"
                      onClick={this.toggleShareDrawer}
                      iconRight={<Icon icon="iconShare" color="#2C405A" />}
                      label="Share"
                    />
                  )
                }
            </div>
          </div>

          <div className="mt-4">
            {
              customFields.map((field, index) => (
                <div
                  className={'row -mt-2'}
                  // eslint-disable-next-line react/no-array-index-key
                  key={`referral-oon-custom-program-input-${index}`}
                >
                  <div className="col-xs-12">
                    <InputField
                      autoComplete="off"
                      className="referral-oon-custom-program-input"
                      id={`input-field-oon-program-group-${index}`}
                      field={field[groupKey]}
                      label="oon-custom-program-input"
                      hideLabel
                      inline={false}
                      onChange={(e) => this.onGroupSelect({ name: e.target.value, id: e.target.value })}
                      ref={this.inputField}
                      placeholder="Type a custom program"
                      validations={!hasAtLeastOneProgram && !hasAtLeastOneCustomProgram ?
                        validations.isRequired : undefined}
                    />
                    <Icon
                      icon="IconCross"
                      className="referral-oon-custom-program-cross"
                      size={8}
                      onClick={() => this.clearCustomField(index)}
                    />
                  </div>
                </div>
              ))
            }

            {
              canAddMoreCustomPrograms({ customFields }) && (
                <Button
                  id="add-another-custom-oon-program-btn"
                  className="mb-one mr-one"
                  label="+ ADD ANOTHER CUSTOM PROGRAM"
                  onClick={this.addCustomField}
                />
              )
            }
          </div>
        </div>
        <div aria-hidden={!showShareDrawer}>
          <ShareDrawer
            employeeId={employeeId}
            coreApi={coreApi}
            SHARES_URL={env.SHARES_URL}
            show={showShareDrawer}
            onClose={this.toggleShareDrawer}
            resourceType={'program'}
            resources={shareDrawerProgramsList}
            onShare={
              ({ shareMethod, language }) => this.onShareEvent({ shareMethod, language })
            }
            providerId={currentProviderId}
            sensitivePrograms={sensitivePrograms}
          />
        </div>
      </div>
    );
  }
}

OONGroupsSelector.propTypes = {
  canPaginateNetworkGroups: PropTypes.bool.isRequired,
  currentProviderId: PropTypes.string.isRequired,
  customFields: PropTypes.array,
  employeeId: PropTypes.string,
  sensitiveProvider: PropTypes.bool,
  caseReferrals: PropTypes.array,
  index: PropTypes.number,
  fields: PropTypes.shape({
    service_type: PropTypes.object.isRequired,
    oonCase: PropTypes.shape({
      selected: PropTypes.array.isRequired,
      custom: PropTypes.array.isRequired,
    }).isRequired,
  }).isRequired,
  debouncedSearchNetworkGroups: PropTypes.func.isRequired,
  originCoordinates: PropTypes.array.isRequired,
  groupKey: PropTypes.string,
  programKey: PropTypes.string,
  onGroupSelect: PropTypes.func,
  oonGroups: PropTypes.array,
  referral: PropTypes.object.isRequired,
  registerField: PropTypes.func.isRequired,
  selectedFields: PropTypes.array,
  removeSelectedBrowseGroup: PropTypes.func.isRequired,
  setHasSensitiveErrors: PropTypes.func,
  toggleBrowse: PropTypes.func.isRequired,
  unregisterField: PropTypes.func,
  isFetchingNetworkGroups: PropTypes.bool,
  setDisableNextButton: PropTypes.func,
};

OONGroupsSelector.defaultProps = {
  customFields: [],
  employeeId: '',
  sensitiveProvider: false,
  caseReferrals: [],
  groupKey: 'group',
  programKey: 'program',
  onGroupSelect: noop,
  oonGroups: [],
  selectedFields: [],
  unregisterField: noop,
  setDisableNextButton: noop,
  isFetchingNetworkGroups: false,
  setHasSensitiveErrors: noop,
  index: 0,
};

const mapStateToProps = (state) => {
  const groupId = state.session.groupId;
  const groupName = getGroup(state, groupId).name;
  const employeeId = state.globalState?.currentEmployee?.id;
  const sensitiveProvider = state.globalState?.currentProvider?.group?.sensitive;
  return {
    currentProviderId: state.session.groupId,
    groupName,
    employeeId,
    sensitiveProvider,
  };
};

export default connect(mapStateToProps)(OONGroupsSelector);
